import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
import { userData } from '../data';
import './persona.css';

import BackgroundCharacter from './components/BackgroundCharacter';
import TopHUD from './components/TopHUD';

import Profile from './pages/Profile';
import Skills from './pages/Skills';
import Experience from './pages/Experience';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Archive from './pages/Archive';
import Contact from './pages/Contact';

const PATH_TO_TAB = {
    '/': 'PROFILE',
    '/projects': 'PROJECTS',
    '/archive': 'ARCHIVE',
    '/career': 'CAREER',
    '/skills': 'SKILLS',
    '/contact': 'CONTACT',
};

const TAB_TO_PATH = {
    'PROFILE': '/',
    'PROJECTS': '/projects',
    'ARCHIVE': '/archive',
    'CAREER': '/career',
    'SKILLS': '/skills',
    'CONTACT': '/contact',
};

export default function PersonaApp() {
    const navigate = useNavigate();
    const location = useLocation();
    const { slug } = useParams();
    const skipLocationSync = useRef(false);
    const mainContentRef = useRef(null);

    const getProjectRouteSlug = useCallback((project) => {
        if (!project) return null;
        if (project.slug) return project.slug;
        return project.name
            ?.toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-') || null;
    }, []);

    const findProjectBySlug = useCallback((projectSlug) => {
        if (!projectSlug) return null;
        return userData.projects.find((project) => getProjectRouteSlug(project) === projectSlug) || null;
    }, [getProjectRouteSlug]);

    const [activeTab, setActiveTab] = useState(() =>
        slug ? 'PROJECTS' : (PATH_TO_TAB[location.pathname] || 'PROFILE')
    );
    const [displayTab, setDisplayTab] = useState(() =>
        slug ? 'PROJECT_DETAIL' : (PATH_TO_TAB[location.pathname] || 'PROFILE')
    );
    const [selectedProject, setSelectedProject] = useState(() =>
        slug ? findProjectBySlug(slug) : null
    );
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [slashAngle, setSlashAngle] = useState(0);
    const isMobile = useIsMobile();
    const [currentTheme, setCurrentTheme] = useState('');
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [fullScreenTransition, setFullScreenTransition] = useState(false);

    useEffect(() => {
        if (skipLocationSync.current) {
            skipLocationSync.current = false;
            return;
        }
        if (slug) {
            const proj = findProjectBySlug(slug);
            setActiveTab('PROJECTS');
            setDisplayTab('PROJECT_DETAIL');
            setSelectedProject(proj);
        } else {
            const tab = PATH_TO_TAB[location.pathname] || 'PROFILE';
            setActiveTab(tab);
            setDisplayTab(tab);
            setSelectedProject(null);
        }
    }, [location.pathname, slug, findProjectBySlug]);

    const menuItems = ["PROFILE", "PROJECTS", "CAREER", "SKILLS", "CONTACT"];
    const transitionAngles = [0, -10, 5, -5, 10];

    const getPathForTab = (tab, payload = null) => {
        if (tab === 'PROJECT_DETAIL') {
            const projectSlug = getProjectRouteSlug(payload);
            return projectSlug ? `/projects/${projectSlug}` : '/projects';
        }
        return TAB_TO_PATH[tab] || '/';
    };

    const handleTabChange = (item, payload = null) => {
        const alreadyHere = item === 'PROJECT_DETAIL'
            ? (displayTab === 'PROJECT_DETAIL' && getProjectRouteSlug(payload) === getProjectRouteSlug(selectedProject))
            : item === displayTab;
        if (alreadyHere || isTransitioning) return;

        const currentlyFull = displayTab === "PROJECT_DETAIL" || displayTab === "ARCHIVE";
        const willBeFull = item === "PROJECT_DETAIL" || item === "ARCHIVE";
        setFullScreenTransition(currentlyFull || willBeFull);

        const newPath = getPathForTab(item, payload);

        if (isMobile) {
            setMobileNavOpen(false);
            setActiveTab(item === "PROJECT_DETAIL" ? "PROJECTS" : item);
            setDisplayTab(item);
            setSelectedProject(payload);
            skipLocationSync.current = true;
            navigate(newPath);
            return;
        }

        const itemIndex = menuItems.indexOf(item);
        setSlashAngle(itemIndex !== -1 ? transitionAngles[itemIndex] : 15);
        setActiveTab(item === "PROJECT_DETAIL" ? "PROJECTS" : item);
        setIsTransitioning(true);

        setTimeout(() => {
            setDisplayTab(item);
            setSelectedProject(payload);
            skipLocationSync.current = true;
            navigate(newPath);
        }, 250);

        setTimeout(() => {
            setIsTransitioning(false);
        }, 700);
    };

    const renderContent = () => {
        switch (displayTab) {
            case "PROFILE": return <Profile />;
            case "PROJECTS": return <Projects onNavigate={handleTabChange} />;
            case "PROJECT_DETAIL": return <ProjectDetail project={selectedProject} onBack={() => handleTabChange("PROJECTS")} />;
            case "ARCHIVE": return <Archive onNavigate={handleTabChange} />;
            case "CAREER": return <Experience />;
            case "SKILLS": return <Skills />;
            case "CONTACT": return <Contact />;
            default: return <Profile />;
        }
    };

    const isFullPage = displayTab === "PROJECT_DETAIL" || displayTab === "ARCHIVE";

    const handleSkipToMain = (event) => {
        event.preventDefault();
        if (!mainContentRef.current) return;

        const activeContentPane = mainContentRef.current.querySelector('.content-pane');
        const focusTarget = activeContentPane || mainContentRef.current;

        focusTarget.focus({ preventScroll: true });
        focusTarget.scrollIntoView({ block: 'start' });
    };

    return (
        <div className={`persona-root min-h-screen w-full relative overflow-hidden flex flex-col md:flex-row font-montserrat ${currentTheme}`}>
            <a href="#main-content" className="skip-link" onClick={handleSkipToMain}>Skip to main content</a>
            <div className="bg-p5-halftone" aria-hidden="true"></div>
            <div className="bg-p5-stripes" aria-hidden="true"></div>

            <TopHUD onThemeChange={setCurrentTheme} currentTheme={currentTheme} />
            <BackgroundCharacter activeTab={displayTab} isFullPage={isFullPage} />

            <button
                className="md:hidden fixed top-2 left-3 z-[200] w-12 h-12 bg-black border-4 border-white shadow-[4px_4px_0_var(--theme-accent)] flex flex-col items-center justify-center gap-1.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FFF200]"
                aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={mobileNavOpen}
                onClick={() => setMobileNavOpen(prev => !prev)}
            >
                <span className={`block w-5 h-0.5 bg-white transition-all duration-200 origin-center ${mobileNavOpen ? 'rotate-45 translate-y-[6px] scale-125' : ''}`} />
                <span className={`block w-5 h-0.5 bg-white transition-all duration-200 ${mobileNavOpen ? 'opacity-0 scale-x-0' : ''}`} />
                <span className={`block w-5 h-0.5 bg-white transition-all duration-200 origin-center ${mobileNavOpen ? '-rotate-45 -translate-y-[10px] scale-125' : ''}`} />
            </button>

            {mobileNavOpen && (
                <div
                    className="md:hidden fixed inset-0 z-[140] bg-black flex flex-col items-center justify-center"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Navigation menu"
                >
                    <div className="bg-p5-halftone" aria-hidden="true"></div>
                    <div className="bg-p5-stripes" aria-hidden="true"></div>
                    <nav className="relative z-10 flex flex-col items-center gap-2" aria-label="Primary navigation">
                        {menuItems.map((item) => (
                            <button
                                key={item}
                                aria-current={activeTab === item ? 'page' : undefined}
                                aria-controls="main-content"
                                onClick={() => handleTabChange(item)}
                                className={`p5-menu-item text-outline border-4 border-black outline-none focus-visible:ring-4 focus-visible:ring-[#FFF200] ${activeTab === item ? 'active' : ''}`}
                            >
                                {item}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => { setMobileNavOpen(false); navigate('/v1'); }}
                            className="mt-6 bg-black text-white font-bebas text-2xl px-6 py-2 border-4 border-white shadow-[6px_6px_0_var(--theme-accent)] tracking-widest hover:bg-white hover:text-black transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FFF200]"
                        >
                            BACK TO V1
                        </button>
                    </nav>
                </div>
            )}

            <div className="flex-1 z-10 flex flex-col md:flex-row relative w-full h-[100dvh] overflow-hidden">
                {!isFullPage && (
                    <nav
                        className="hidden md:flex w-full md:w-1/4 md:h-full md:flex-col md:justify-center md:items-end gap-4 md:pr-16 2xl:pr-24 relative z-30 shrink-0 md:order-last"
                        aria-label="Primary navigation"
                    >
                        {menuItems.map((item) => (
                            <button
                                key={item}
                                aria-current={activeTab === item ? 'page' : undefined}
                                aria-controls="main-content"
                                onClick={() => handleTabChange(item)}
                                className={`p5-menu-item text-outline border-[6px] border-black ${activeTab === item ? 'active' : ''}`}
                            >
                                {item}
                            </button>
                        ))}
                    </nav>
                )}

                <main
                    id="main-content"
                    ref={mainContentRef}
                    tabIndex={-1}
                    className={`${isFullPage ? 'w-full' : 'w-full md:w-3/4'} flex-1 min-h-0 overflow-x-hidden ${isTransitioning ? 'overflow-y-hidden' : 'overflow-y-auto'} px-2 sm:px-4 md:pl-10 lg:pl-20 2xl:pl-28 md:pr-10 2xl:pr-14 pt-4 md:pt-0 pb-24 md:pb-0 2xl:pb-8 flex flex-col relative md:order-first`}
                    role="main"
                    aria-label="Main content"
                >
                    <div className={`${fullScreenTransition ? 'fixed' : 'absolute'} inset-0 overflow-hidden pointer-events-none z-[100]`} aria-hidden="true" style={{ '--slash-angle': `${slashAngle}deg` }}>
                        <div className={`jaw-wrapper ${isTransitioning ? 'active' : ''}`}>
                            <div className="slice-line"></div>
                            <div className="jaw-panel jaw-top"></div>
                            <div className="jaw-panel jaw-bottom"></div>
                        </div>
                    </div>

                    {renderContent()}
                </main>
            </div>

        </div>
    );
}